/**
 * QR encoding for the pairing code, built on ZXing's JavaScript port
 * (`@zxing/library`, Apache-2.0). The library is loaded on demand so it only
 * ships in a lazy chunk fetched when a user pairs a device.
 */

/** Square module grid; `true` = dark module. Excludes the quiet zone. */
export type QrMatrix = boolean[][]

export async function encodeQrMatrix(text: string): Promise<QrMatrix> {
  const { QRCodeEncoder, QRCodeDecoderErrorCorrectionLevel, EncodeHintType } =
    await import('@zxing/library')
  const hints = new Map()
  // The payload is pure ASCII (URL + base64url), so byte mode in ISO-8859-1
  // round-trips exactly.
  hints.set(EncodeHintType.CHARACTER_SET, 'ISO-8859-1')
  const code = QRCodeEncoder.encode(text, QRCodeDecoderErrorCorrectionLevel.M, hints)
  const matrix = code.getMatrix()
  const width = matrix.getWidth()
  const height = matrix.getHeight()
  const rows: QrMatrix = []
  for (let y = 0; y < height; y += 1) {
    const row: boolean[] = []
    for (let x = 0; x < width; x += 1) row.push(matrix.get(x, y) === 1)
    rows.push(row)
  }
  return rows
}

/**
 * SVG path data drawing every dark module as one unit square, offset by the
 * quiet zone, coalescing horizontal runs to keep the path short.
 */
export function qrMatrixPath(matrix: QrMatrix, quietZone: number): string {
  const parts: string[] = []
  matrix.forEach((row, y) => {
    let x = 0
    while (x < row.length) {
      if (!row[x]) {
        x += 1
        continue
      }
      const start = x
      while (x < row.length && row[x]) x += 1
      parts.push(`M${start + quietZone} ${y + quietZone}h${x - start}v1h-${x - start}z`)
    }
  })
  return parts.join('')
}
